import { useState, useRef, useEffect } from "react";
import { db, auth } from "./firebase";
import {
  collection, onSnapshot, query, where, orderBy,
  addDoc, updateDoc, doc, serverTimestamp
} from "firebase/firestore";

const DOC_TYPES = ["SIA Licence","DBS Certificate","Right to Work","Contract","Training Certificate","Insurance Document","Medical Certificate","Other"];
const STATUS = {
  valid:    { color:"#22C55E", bg:"#22C55E18", label:"Valid" },
  expiring: { color:"#F59E0B", bg:"#F59E0B18", label:"Expiring" },
  expired:  { color:"#EF4444", bg:"#EF444418", label:"Expired" },
  missing:  { color:"#6B7280", bg:"#6B728018", label:"Missing" },
};
const fmt = d => new Date(d).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});
const daysLeft = d => Math.ceil((new Date(d)-new Date())/(1000*60*60*24));

function getStatus(expiryStr) {
  const d = daysLeft(expiryStr);
  if (d < 0)   return "expired";
  if (d <= 90) return "expiring";
  return "valid";
}

export default function DocumentVault() {
  const tenantId = auth.currentUser?.tenantId || "osecurity-ltd";

  // â”€â”€ Real docs from Firestore â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [allDocs, setAllDocs]   = useState([]);
  const [officers, setOfficers] = useState([]);

  useEffect(() => {
    const q = query(
      collection(db, "documents"),
      where("tenant_id", "==", tenantId),
      orderBy("uploaded_at", "desc")
    );
    return onSnapshot(q, snap => {
      const docs = snap.docs.map(d => {
        const data = d.data();
        const expiryStr = data.expiry_date?.toDate?.().toISOString().split("T")[0] || data.expiry_date || "";
        return {
          id: d.id,
          uid: data.uploaded_by || "",
          name: data.officer_name || data.officer_email || "Unknown",
          role: data.officer_role || "Officer",
          type: data.doc_type || "Document",
          status: expiryStr ? getStatus(expiryStr) : "valid",
          expiry: expiryStr,
          uploaded: data.uploaded_at?.toDate?.().toISOString().split("T")[0] || "",
          size: data.file_size || "",
        };
      });
      setAllDocs(docs);

      // Build officers list from docs
      const byUid = {};
      docs.forEach(d => {
        if (!byUid[d.uid]) byUid[d.uid] = { uid:d.uid, name:d.name, role:d.role, docs:[] };
        byUid[d.uid].docs.push(d);
      });
      setOfficers(Object.values(byUid));
    });
  }, [tenantId]);

  // â”€â”€ SIA Alerts from Firestore â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [siaAlerts, setSiaAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);

  useEffect(() => {
    const q = query(
      collection(db, "sia_alerts"),
      where("tenant_id", "==", tenantId),
      where("dismissed", "==", false),
      orderBy("days_remaining", "asc")
    );
    return onSnapshot(q, snap => {
      setSiaAlerts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
  }, [tenantId]);

  const dismissAlert = async (alertId) => {
    await updateDoc(doc(db, "sia_alerts", alertId), { dismissed: true });
  };

  // â”€â”€ UI state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [sel, setSel]       = useState(null);
  const [fStatus, setFS]    = useState("all");
  const [fType, setFT]      = useState("all");
  const [search, setSearch] = useState("");
  const [showUp, setShowUp] = useState(false);

  // Upload form state
  const [upOfficer, setUpOfficer]   = useState("");
  const [upType, setUpType]         = useState(DOC_TYPES[0]);
  const [upExpiry, setUpExpiry]     = useState("");
  const [upFile, setUpFile]         = useState(null);
  const [uploading, setUploading]   = useState(false);
  const fileRef = useRef();

  const docs = allDocs.filter(d => {
    if (fStatus !== "all" && d.status !== fStatus) return false;
    if (fType   !== "all" && d.type   !== fType)   return false;
    if (search && !d.name.toLowerCase().includes(search.toLowerCase()) &&
        !d.type.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total:    allDocs.length,
    valid:    allDocs.filter(d => d.status === "valid").length,
    expiring: allDocs.filter(d => d.status === "expiring").length,
    expired:  allDocs.filter(d => d.status === "expired").length,
  };

  const compliance = officers.map(o => {
    const req = ["SIA Licence","DBS Certificate","Right to Work"];
    const score = Math.round(req.filter(r => o.docs.find(d => d.type === r)).length / req.length * 100);
    return {
      ...o, score,
      expired:  o.docs.filter(d => d.status === "expired").length,
      expiring: o.docs.filter(d => d.status === "expiring").length,
      missing:  req.filter(r => !o.docs.find(d => d.type === r)),
    };
  });

  const handleUpload = async () => {
    if (!upFile || !upExpiry) return;
    setUploading(true);
    try {
      await addDoc(collection(db, "documents"), {
        tenant_id:    tenantId,
        uploaded_by:  auth.currentUser?.uid || "",
        officer_name: upOfficer || auth.currentUser?.email || "",
        officer_email:auth.currentUser?.email || "",
        doc_type:     upType,
        expiry_date:  new Date(upExpiry),
        file_name:    upFile.name,
        file_size:    (upFile.size / 1024).toFixed(0) + "KB",
        uploaded_at:  serverTimestamp(),
      });
      setShowUp(false);
      setUpFile(null);
      setUpExpiry("");
    } catch(e) {
      console.error(e);
    }
    setUploading(false);
  };

  const S = {
    page:  { padding:"0 24px 24px", color:"#F8FAFC" },
    hdr:   { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 0 16px" },
    grid4: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 },
    card:  { background:"#0A1628", border:"1px solid #1E3A5F", borderRadius:8, padding:16, marginBottom:12 },
    btn:   (c,o) => ({ background:o?"transparent":c, color:o?c:"#fff", border:`1px solid ${c}`, padding:"7px 16px", borderRadius:6, cursor:"pointer", fontSize:12, fontWeight:700 }),
    badge: (s)  => ({ background:STATUS[s]?.bg, color:STATUS[s]?.color, padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700 }),
    th:    { fontSize:9, color:"#9CA3AF", letterSpacing:2, textTransform:"uppercase", padding:"8px 12px", borderBottom:"1px solid #1E3A5F", textAlign:"left" },
    td:    { fontSize:12, color:"#F8FAFC", padding:"10px 12px", borderBottom:"1px solid #1E3A5F11" },
    inp:   { background:"#0A1628", border:"1px solid #1E3A5F", color:"#F8FAFC", padding:"8px 12px", borderRadius:6, fontSize:12, width:"100%" },
    sel:   { background:"#0A1628", border:"1px solid #1E3A5F", color:"#F8FAFC", padding:"8px 12px", borderRadius:6, fontSize:12 },
  };

  return (
    <div style={S.page}>
      <div style={S.hdr}>
        <div>
          <div style={{fontSize:22,fontWeight:900,letterSpacing:1}}>DOCUMENT VAULT</div>
          <div style={{fontSize:11,color:"#9CA3AF",marginTop:2,letterSpacing:2}}>BS 7858 COMPLIANCE - STAFF DOCUMENT MANAGEMENT</div>
        </div>
        <button style={S.btn("#C41E3A")} onClick={() => setShowUp(true)}>+ Upload Document</button>
      </div>

      {/* Tab Bar */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        <button onClick={() => setShowAlerts(false)} style={{
          padding:"6px 18px",borderRadius:6,border:"none",cursor:"pointer",
          background:!showAlerts?"#14b8a6":"#1e293b",color:"#fff",fontWeight:600,fontSize:13
        }}>Documents</button>
        <button onClick={() => setShowAlerts(true)} style={{
          padding:"6px 18px",borderRadius:6,border:"none",cursor:"pointer",
          background:showAlerts?"#ef4444":"#1e293b",color:"#fff",fontWeight:600,fontSize:13,
          position:"relative"
        }}>
          SIA Alerts
          {siaAlerts.length > 0 && (
            <span style={{
              position:"absolute",top:-7,right:-7,background:"#ef4444",color:"#fff",
              borderRadius:"50%",width:19,height:19,fontSize:11,
              display:"flex",alignItems:"center",justifyContent:"center",
              border:"2px solid #0f172a"
            }}>{siaAlerts.length}</span>
          )}
        </button>
      </div>

      {/* SIA Alerts Panel */}
      {showAlerts && (
        <div style={S.card}>
          {siaAlerts.length === 0 ? (
            <p style={{color:"#94a3b8",textAlign:"center",padding:48}}>All clear â€” no expiry alerts</p>
          ) : siaAlerts.map(alert => {
            const expDate = alert.expiry_date?.toDate?.() || new Date(alert.expiry_date);
            const borderColor = alert.days_remaining <= 7 ? "#ef4444" : alert.days_remaining <= 30 ? "#f59e0b" : "#3b82f6";
            return (
              <div key={alert.id} style={{
                background:"#1e293b",borderRadius:10,padding:16,marginBottom:12,
                borderLeft:`4px solid ${borderColor}`,
                display:"flex",justifyContent:"space-between",alignItems:"center"
              }}>
                <div>
                  <p style={{color:"#f1f5f9",fontWeight:700,margin:0,fontSize:15}}>
                    {alert.officer_name} â€” {alert.doc_type}
                  </p>
                  <p style={{color:"#94a3b8",fontSize:13,margin:"4px 0 0"}}>
                    Expires: {expDate.toLocaleDateString("en-GB")}
                    {" Â· "}
                    <span style={{color:borderColor,fontWeight:700}}>{alert.days_remaining} days remaining</span>
                  </p>
                </div>
                <button onClick={() => dismissAlert(alert.id)} style={{
                  background:"#334155",color:"#94a3b8",border:"none",
                  borderRadius:6,padding:"5px 12px",cursor:"pointer",fontSize:12
                }}>Dismiss</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Documents View */}
      {!showAlerts && (
        <div>
          {stats.expired > 0 && (
            <div style={{background:"#EF444418",border:"1px solid #EF4444",borderRadius:8,padding:"12px 16px",marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:18}}>âš ï¸</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:700,color:"#EF4444"}}>{stats.expired} expired document{stats.expired>1?"s":""} require immediate attention</div>
                <div style={{fontSize:11,color:"#9CA3AF"}}>Officers with expired documents may not be legally compliant to work</div>
              </div>
              <button style={S.btn("#EF4444")} onClick={() => setFS("expired")}>View Expired</button>
            </div>
          )}

          <div style={S.grid4}>
            {[["total","#F8FAFC","Total"],["valid","#22C55E","Valid"],["expiring","#F59E0B","Expiring"],["expired","#EF4444","Expired"]].map(([k,c,l]) => (
              <div key={k} style={{background:"#0A1628",border:`1px solid ${c}33`,borderRadius:8,padding:"14px 16px",cursor:"pointer"}} onClick={() => setFS(k==="total"?"all":k)}>
                <div style={{fontSize:32,fontWeight:900,color:c,lineHeight:1}}>{stats[k]}</div>
                <div style={{fontSize:10,color:"#9CA3AF",letterSpacing:2,textTransform:"uppercase",marginTop:4}}>{l}</div>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1.6fr",gap:16}}>
            <div style={S.card}>
              <div style={{fontSize:10,color:"#9CA3AF",letterSpacing:2,marginBottom:12,textTransform:"uppercase"}}>Officer Compliance</div>
              {compliance.length === 0 && <p style={{color:"#94a3b8",fontSize:13}}>No documents uploaded yet.</p>}
              {compliance.map(o => (
                <div key={o.uid} style={{padding:"10px 0",borderBottom:"1px solid #1E3A5F22",cursor:"pointer"}} onClick={() => setSel(o)}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                    <div><span style={{fontSize:13,fontWeight:700}}>{o.name}</span><span style={{fontSize:10,color:"#9CA3AF",marginLeft:8}}>{o.role}</span></div>
                    <span style={{fontSize:13,fontWeight:900,color:o.score===100?"#22C55E":o.score>=66?"#F59E0B":"#EF4444"}}>{o.score}%</span>
                  </div>
                  <div style={{background:"#1E3A5F22",borderRadius:2,height:4,marginBottom:6}}>
                    <div style={{height:4,borderRadius:2,background:o.score===100?"#22C55E":o.score>=66?"#F59E0B":"#EF4444",width:`${o.score}%`}}/>
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {o.expired>0 && <span style={S.badge("expired")}>{o.expired} EXPIRED</span>}
                    {o.expiring>0 && <span style={S.badge("expiring")}>{o.expiring} EXPIRING</span>}
                    {o.missing.length>0 && <span style={S.badge("missing")}>{o.missing.length} MISSING</span>}
                    {!o.expired&&!o.expiring&&!o.missing.length && <span style={S.badge("valid")}>COMPLIANT</span>}
                  </div>
                </div>
              ))}
            </div>

            <div style={S.card}>
              <div style={{display:"flex",gap:8,marginBottom:12}}>
                <input style={{...S.inp,flex:1}} placeholder="Search officer or document..." value={search} onChange={e=>setSearch(e.target.value)}/>
                <select style={S.sel} value={fType} onChange={e=>setFT(e.target.value)}>
                  <option value="all">All Types</option>
                  {DOC_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                </select>
                <select style={S.sel} value={fStatus} onChange={e=>setFS(e.target.value)}>
                  <option value="all">All Status</option>
                  {Object.keys(STATUS).map(s=><option key={s} value={s}>{STATUS[s].label}</option>)}
                </select>
              </div>
              <div style={{overflowY:"auto",maxHeight:380}}>
                {docs.length === 0 ? (
                  <p style={{color:"#94a3b8",textAlign:"center",padding:32,fontSize:13}}>No documents match your filters.</p>
                ) : (
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr>
                      <th style={S.th}>Officer</th>
                      <th style={S.th}>Document</th>
                      <th style={S.th}>Expiry</th>
                      <th style={S.th}>Status</th>
                    </tr></thead>
                    <tbody>
                      {docs.map(d => (
                        <tr key={d.id}>
                          <td style={S.td}><div style={{fontWeight:600}}>{d.name}</div><div style={{fontSize:10,color:"#9CA3AF"}}>{d.role}</div></td>
                          <td style={S.td}><div>{d.type}</div><div style={{fontSize:10,color:"#9CA3AF"}}>{d.size}</div></td>
                          <td style={S.td}>{d.expiry ? (<><div>{fmt(d.expiry)}</div><div style={{fontSize:10,color:d.status==="expired"?"#EF4444":"#9CA3AF"}}>{daysLeft(d.expiry)<0?`${Math.abs(daysLeft(d.expiry))}d overdue`:`${daysLeft(d.expiry)}d left`}</div></>) : "â€”"}</td>
                          <td style={S.td}><span style={S.badge(d.status)}>{STATUS[d.status]?.label}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {sel && (
            <div style={{...S.card,marginTop:16}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div><div style={{fontSize:16,fontWeight:800}}>{sel.name}</div><div style={{fontSize:11,color:"#9CA3AF"}}>{sel.role} Â· Document Record</div></div>
                <button style={S.btn("#1E3A5F",true)} onClick={() => setSel(null)}>âœ• Close</button>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {["SIA Licence","DBS Certificate","Right to Work"].map(type => {
                  const d = sel.docs.find(x => x.type === type);
                  return (
                    <div key={type} style={{background:"#111827",border:`1px solid ${d?STATUS[d.status]?.color+"44":"#6B728044"}`,borderRadius:8,padding:14}}>
                      <div style={{fontSize:10,color:"#9CA3AF",letterSpacing:1,marginBottom:8,textTransform:"uppercase"}}>{type}</div>
                      {d ? (<>
                        <span style={S.badge(d.status)}>{STATUS[d.status]?.label}</span>
                        <div style={{fontSize:11,color:"#9CA3AF",marginTop:8}}>Expires: {fmt(d.expiry)}</div>
                        <div style={{fontSize:11,color:"#9CA3AF"}}>Uploaded: {fmt(d.uploaded)}</div>
                      </>) : (<>
                        <span style={S.badge("missing")}>MISSING</span>
                        <div style={{fontSize:11,color:"#EF4444",marginTop:8}}>Required for compliance</div>
                        <button style={{...S.btn("#C41E3A"),fontSize:10,padding:"4px 10px",marginTop:10}} onClick={() => setShowUp(true)}>+ Upload</button>
                      </>)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUp && (
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"#060B14ee",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:"#0A1628",border:"1px solid #C41E3A",borderRadius:12,padding:28,width:460}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:20}}>
              <div style={{fontSize:16,fontWeight:800}}>Upload Document</div>
              <button onClick={() => setShowUp(false)} style={{background:"none",border:"none",color:"#9CA3AF",cursor:"pointer",fontSize:18}}>âœ•</button>
            </div>
            <label style={{fontSize:10,color:"#9CA3AF",letterSpacing:2,display:"block",marginBottom:6,textTransform:"uppercase"}}>Officer Name</label>
            <input style={{...S.inp,marginBottom:14}} placeholder="Officer full name" value={upOfficer} onChange={e=>setUpOfficer(e.target.value)}/>
            <label style={{fontSize:10,color:"#9CA3AF",letterSpacing:2,display:"block",marginBottom:6,textTransform:"uppercase"}}>Document Type</label>
            <select style={{...S.sel,width:"100%",marginBottom:14}} value={upType} onChange={e=>setUpType(e.target.value)}>
              {DOC_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
            <label style={{fontSize:10,color:"#9CA3AF",letterSpacing:2,display:"block",marginBottom:6,textTransform:"uppercase"}}>Expiry Date</label>
            <input type="date" style={{...S.inp,marginBottom:14}} value={upExpiry} onChange={e=>setUpExpiry(e.target.value)}/>
            <div style={{border:"2px dashed #1E3A5F",borderRadius:8,padding:24,textAlign:"center",cursor:"pointer",marginBottom:20}} onClick={() => fileRef.current?.click()}>
              <div style={{fontSize:24,marginBottom:8}}>ðŸ“„</div>
              <div style={{fontSize:12,color:"#9CA3AF"}}>{upFile ? upFile.name : "Click to browse or drag file here"}</div>
              <div style={{fontSize:10,color:"#6B7280",marginTop:4}}>PDF, JPG or PNG Â· max 10MB</div>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{display:"none"}} onChange={e=>setUpFile(e.target.files?.[0]||null)}/>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btn("#1E3A5F",true),flex:1}} onClick={() => setShowUp(false)}>Cancel</button>
              <button style={{...S.btn("#C41E3A"),flex:2,opacity:uploading?0.6:1}} onClick={handleUpload} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload Document"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

